import { useQuery } from "@tanstack/react-query";
import type { User, CandidateProfile, CompanyProfile } from "@shared/schema";
import { getQueryFn, initializeUserCache } from "@/lib/queryClient";
import { useEffect } from "react";

export type UserWithProfile = User & {
  profile?: CandidateProfile | CompanyProfile | null;
};

/**
 * Detect whether a Stytch session cookie is present on the client.
 *
 * Stytch's frontend SDK writes its session cookies (stytch_b2b_jwt,
 * stytch_consumer_jwt, stytch_session_jwt, and their non-_jwt variants) without
 * the httpOnly flag, so they are readable here. When none are present the visitor
 * is definitively unauthenticated, so we skip the /api/auth/user request entirely.
 * This avoids the browser logging a 401 "Failed to load resource" console error on
 * public marketing pages, where every visitor is anonymous. Logged-in users still
 * carry a cookie, so their session check runs unchanged.
 */
export function hasStytchSessionCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((pair) => {
    const name = pair.split("=")[0]?.trim() ?? "";
    return name.startsWith("stytch_") && (name.includes("session") || name.includes("jwt"));
  });
}

export function useAuth() {
  const sessionCookiePresent = hasStytchSessionCookie();

  const authQuery = useQuery<UserWithProfile | null>({
    queryKey: ["/api/auth/user"],
    queryFn: getQueryFn({ on401: "returnNull" }), // Return null for unauthenticated users instead of throwing
    enabled: sessionCookiePresent, // Skip the request for anonymous visitors (no session cookie -> guaranteed 401)
    retry: false,
    staleTime: 30 * 60 * 1000, // 30 minutes (much longer for auth data)
    gcTime: 60 * 60 * 1000, // 1 hour garbage collection
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
    refetchOnReconnect: false, // Don't refetch on network reconnect
  });

  const { data: user, isLoading, isError, status, refetch } = authQuery;

  // SECURITY: Initialize user-scoped cache when user is authenticated
  useEffect(() => {
    if (user?.id && !isLoading && !isError) {
      initializeUserCache(user.id).catch(error => {
        console.error('[Security] Failed to initialize user cache:', error);
      });
    }
  }, [user?.id, isLoading, isError]);

  // Auth is ready when the query has completed (either success with user data or error for unauthenticated)
  // Since we use on401: "throw", 401 responses put the query in error state
  // This prevents treating initial 401s as "ready" with null data
  const authReady = !isLoading;

  return {
    user,
    isLoading,
    isAuthenticated: !!user && !isError,
    // Track whether auth query has received a definitive response
    // This prevents routing evaluation during transient 401 responses and handshakes
    authReady,
  };
}
